import React, { FC, useEffect, useRef } from "react";
import { isBrowser } from "@montana/helpers/browser.helper";

interface IClickOutsideDetectProps {
    onClickOutside: (event?: MouseEvent) => void;
    onClickInside?: (event?: MouseEvent) => void;
    onFocusOutside?: (event?: FocusEvent) => void;
    onFocusInside?: (event?: FocusEvent) => void;
    // use focusin events as well as click outside (true by default
    useFocus?: boolean;
    style?: React.CSSProperties;
    className?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children: any;
}

export const ClickOutsideDetect: FC<IClickOutsideDetectProps> = (props: IClickOutsideDetectProps): JSX.Element => {
    const { children, className = "", style, useFocus } = props;
    const clickRef = useRef<HTMLDivElement>(null);

    const handleOutsideClick = (): void => {
        if (typeof props.onClickOutside === 'function') {
            props.onClickOutside();
        }
    };

    const handleInsideClick = (event: React.MouseEvent<HTMLDivElement>): void => {
        event.stopPropagation();
        if (typeof props.onClickInside === 'function') {
            props.onClickInside();
        }
    };

    const handleFocusOutside = (): void => {
        if (typeof props.onFocusOutside === 'function') {
            props.onFocusOutside();
        } else if (typeof props.onClickOutside === 'function') {
            props.onClickOutside();
        }
    };

    const handleFocusInside = (event: FocusEvent): void => {
        event.stopPropagation();
        if (typeof props.onFocusInside === 'function') {
            props.onFocusInside();
        } else if (typeof props.onClickInside === 'function') {
            props.onClickInside();
        }
    };

    const mountListeners = (): void => {
        if (clickRef.current && useFocus !== false) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            clickRef.current.addEventListener('focusin', handleFocusInside as any);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            window.addEventListener('focusin', handleFocusOutside as any);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        window.addEventListener('click', handleOutsideClick as any);
    };

    const unmountListeners = (): void => {
        if (clickRef.current && useFocus !== false) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            clickRef.current.removeEventListener('focusin', handleFocusInside as any);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            window.removeEventListener('focusin', handleFocusOutside as any);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        window.removeEventListener('click', handleOutsideClick as any);
    };

    useEffect((): () => void => {
        if (isBrowser) {
            // Timeout is used to add event listener after an eventual click event used to spawn the container has finished it's bubbling phase.
            setTimeout(mountListeners, 0);

            return unmountListeners;
        }

        return (): void => undefined;
    }, []);


    return (
        // Normally we'd not add a click handler to a DIV-tag, so the a11y warnings are sound, but in this case it's
        // purely there for detection reasons, so it's a safe bet to disable the warnings.
        //
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
        <div className={className} style={style} ref={clickRef} onClick={handleInsideClick}>
            {children}
        </div>
    );
};
